import { useEffect, useRef, useState } from 'react'
import { KEEP_HOURS, reportsSignIn } from './api'

type Step = 'id' | 'pin' | 'new' | 'again'
const LEN: Record<Step, number> = { id: 4, pin: 6, new: 6, again: 6 }

/** R0: an owner's four-digit employee ID, then their own six-digit Reports PIN. A temporary PIN (handed out under Team on the
 *  counter app) leads straight to picking your own. "Change my PIN" does the same for a PIN that is already yours.
 *  Three wrong tries lock Reports for 15 minutes. */
export function Access({ onOpen, other }: { onOpen: () => void; other: boolean }) {
  const [step, setStep] = useState<Step>('id')
  const [changing, setChanging] = useState(false)   // "Change my PIN": ask for the new one after the current one
  const [id, setId] = useState('')
  const [pin, setPin] = useState('')
  const [first, setFirst] = useState('')
  const [val, setVal] = useState('')
  const [name, setName] = useState('')
  const [remember, setRemember] = useState(true)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [locked, setLocked] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => { input.current?.focus() }, [step])

  const fail = (m: string, back: Step) => {
    setMsg(m); setVal('')
    if (/locked/i.test(m)) setLocked(true)
    setStep(back)
    input.current?.focus()
  }
  const reset = (keepChanging = false) => { setStep('id'); setId(''); setPin(''); setFirst(''); setVal(''); setName(''); setMsg(''); if (!keepChanging) setChanging(false) }

  const signIn = async (p: string, newPin?: string) => {
    setBusy(true); setMsg('')
    try {
      const r = await reportsSignIn(id, p, remember, newPin)
      if (r.setPin) { setName(r.name); setPin(p); setVal(''); setStep('new'); return }
      onOpen()
    } catch (e) {
      const m = (e as Error).message
      if (/weak|digit repeated|run like|different from/i.test(m)) { setFirst(''); fail(m, 'new') }
      else { setPin(''); setFirst(''); fail(m, 'pin') }
    } finally { setBusy(false) }
  }

  const done = (c: string) => {
    if (busy) return
    if (step === 'id') { setId(c); setVal(''); setMsg(''); setStep('pin'); return }
    if (step === 'pin') { if (changing) { setPin(c); setVal(''); setMsg(''); setStep('new') } else signIn(c); return }
    if (step === 'new') { setFirst(c); setVal(''); setMsg(''); setStep('again'); return }
    if (c !== first) { setFirst(''); fail('The two PINs differ. Pick your new PIN again.', 'new'); return }
    signIn(pin, c)
  }
  const onChange = (v: string) => {
    const c = v.replace(/[^0-9]/g, '').slice(0, LEN[step])
    setVal(c)
    if (c.length === LEN[step]) done(c)
  }

  const setting = step === 'new' || step === 'again'
  const title = locked ? 'Reports is locked for 15 minutes'
    : step === 'id' ? (changing ? 'Change my PIN: enter your employee ID' : 'Enter your employee ID')
    : step === 'pin' ? (changing ? 'Enter your current Reports PIN' : 'Enter your Reports PIN')
    : step === 'new' ? (name && !changing ? `Welcome, ${name}. Pick your own six-digit PIN` : 'Pick your new six-digit PIN')
    : 'Type your new PIN again'
  const n = LEN[step]
  return (
    <div className="rp-access" onClick={() => input.current?.focus()}>
      <div className="rp-access__box">
        <div className="rp-access__logo"><img src="/brand/star-black.png" alt="Starchive" /></div>
        <div className="rp-access__title"><h1>Starchive Reports</h1><span>{title}</span></div>
        <label htmlFor="rp-code" className="rp-visually-hidden">{step === 'id' ? 'Employee ID, four digits' : 'Reports PIN, six digits'}</label>
        <input id="rp-code" ref={input} type={step === 'id' ? 'text' : 'password'} inputMode="numeric" autoComplete={setting ? 'new-password' : 'one-time-code'} className="rp-access__input" value={val} onChange={(e) => onChange(e.target.value)} disabled={busy || locked} aria-describedby="rp-access-msg" />
        <div className="rp-access__boxes" aria-hidden="true">
          {Array.from({ length: n }, (_, i) => <div key={i} className={'rp-access__digit' + (i < val.length ? ' is-filled' : '') + (i === val.length && !busy ? ' is-cur' : '')}>{i < val.length && (step === 'id' ? <b>{val[i]}</b> : <span />)}</div>)}
        </div>
        <div id="rp-access-msg" className={'rp-access__msg' + (msg ? ' is-bad' : '')} role="status">{busy ? 'Checking…' : msg || (step !== 'id' && id ? `ID ${id}` : other ? 'This computer holds a counter session; your ID and Reports PIN open Reports on their own.' : '')}</div>
        {!setting && <label className="rp-access__remember"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />Keep me signed in on this computer for {KEEP_HOURS} hours</label>}
        <button type="button" className="rp-access__open" disabled={val.length !== n || busy || locked} onClick={() => done(val)}>{step === 'id' ? 'Next' : step === 'pin' && !changing ? 'Open Reports' : step === 'again' ? 'Save PIN and open Reports' : 'Next'}</button>
        <div className="rp-access__links">
          {step !== 'id' && <button type="button" className="rp-access__link" onClick={(e) => { e.stopPropagation(); reset(changing) }}>Start over</button>}
          {!setting && <button type="button" className="rp-access__link" onClick={(e) => { e.stopPropagation(); const c = !changing; reset(); setChanging(c) }}>{changing ? 'Just sign in' : 'Change my PIN'}</button>}
        </div>
        <div className="rp-access__foot">
          <span>Each owner has their own six-digit Reports PIN, separate from the counter. A temporary PIN from Team on the counter app is replaced with your own the first time you sign in.</span>
          <span>Three wrong tries lock Reports for 15 minutes and are written to the audit log. Forgot your PIN? Another owner gives you a new temporary one under Team.</span>
        </div>
      </div>
      <div className="rp-access__host">{window.location.hostname}</div>
    </div>
  )
}
